import useSWRInfinite from "swr/infinite";
import useSWRMutation from "swr/mutation";
import useIsAllowed from "../../../auth/accessToken/hooks/useIsAllowed";
import { useAccountId } from "../../../auth/useAccountId";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type { SalesLocation } from "../../../types/customers";

export type SalesLocationQuery = {
    /**
     * 1..100, default is 10
     */
    limit: number;
    /**
     * location_id of the last location in the previous page
     */
    starting_after?: string;

    /**
     * search term matching name, phone_number, email, organization_number or business_name
     */
    search?: string;

    status?: string[];

    country?: string[];

    phone_number?: string[];

    organization_number?: string[];

    email?: string[];

    type?: string[];
};

type SalesLocationCollection = {
    locations: SalesLocation[];
    hasNext: boolean;
    cursor?: string;
};

const fetchLocations = async (payload: {
    accountId: string;
    params: string;
    startingAfter?: string;
}): Promise<SalesLocationCollection> => {
    const parsedParams = JSON.parse(payload.params) as SalesLocationQuery;
    const stringifiedParams = new URLSearchParams();
    Object.entries(parsedParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((v) => stringifiedParams.append(key, v));
        } else {
            stringifiedParams.append(
                key,
                typeof value === "string" ? value : value.toString(),
            );
        }
    });
    if (payload.startingAfter) {
        stringifiedParams.append("starting_after", payload.startingAfter);
    }
    let limit = Number.parseInt(stringifiedParams.get("limit") as string);
    if (Number.isNaN(limit)) {
        limit = 10;
    }
    return await fulfill.get({
        accountId: payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${payload.accountId}/locations?${stringifiedParams.toString()}`,
        cacheBuster: false,
        handlers: {
            200: (data: SalesLocation[]) => {
                const hasNext = data.length === limit && data.length > 0;
                return {
                    locations: data,
                    hasNext: hasNext,
                    cursor: data?.[data.length - 1]?.location_id,
                };
            },
            404: () => {
                return {
                    locations: [],
                    hasNext: false,
                };
            },
        },
    });
};

const fetchPostLocations = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            location: Partial<SalesLocation>;
        };
    },
): Promise<SalesLocation> => {
    return await fulfill.post<Partial<SalesLocation>>({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/locations`,
        json: options.arg.location,
        handlers: {
            200: (data) => data,
        },
    });
};

const getKey =
    (
        canAccessLocations: boolean,
        accountId: string,
        params?: SalesLocationQuery,
    ) =>
    (pageIndex: number, previousPageData: SalesLocationCollection | null) => {
        if (!canAccessLocations) {
            return null;
        }
        if (previousPageData && !previousPageData.hasNext) {
            return null;
        }
        return {
            key: `${accountId}/locations/${pageIndex}`,
            accountId,
            params: JSON.stringify(params),
            startingAfter: previousPageData?.cursor,
        };
    };

export const useLocations = (params?: SalesLocationQuery) => {
    const canAccessLocations = useIsAllowed([
        ["admin:accounts"],
        ["admin:locations"],
        ["read:locations"],
    ]);
    const accountId = useAccountId();
    const { data, isLoading, setSize, mutate } = useSWRInfinite(
        getKey(canAccessLocations, accountId, params),
        fetchLocations,
    );
    const {
        trigger: createLocation,
        isMutating: isCreatingLocation,
        error: createLocationError,
    } = useSWRMutation(
        () => (canAccessLocations ? `${accountId}/locations` : null),
        fetchPostLocations,
        {
            onSuccess: () => {
                mutate();
            },
        },
    );

    const locations = data?.flatMap((l) => l.locations) ?? [];
    const hasNext = data?.[data.length - 1]?.hasNext ?? false;

    const next = () => {
        if (hasNext) {
            setSize((s) => s + 1);
        }
    };

    const refresh = () => {
        setSize(1);
    };

    return {
        locations,
        isLoading,
        hasNext,
        next,
        refresh,
        createLocation,
        isCreatingLocation,
        createLocationError,
    };
};
